/**
 * Content registry — maps slugs to Markdown modules.
 * Eager glob keeps routing simple and enables static builds without a CMS.
 */

export type PageMeta = {
  title: string;
  description: string;
  /** Optional section label shown above the H1 */
  eyebrow?: string;
};

export type PageModule = {
  meta: PageMeta;
  markdown: string;
};

const rawPages = import.meta.glob('../../content/**/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

function pathToSlug(path: string): string {
  return path
    .replace('../../content/', '')
    .replace(/\.md$/, '');
}

function parseFrontmatter(source: string): {
  meta: Partial<PageMeta>;
  body: string;
} {
  if (!source.startsWith('---')) {
    return { meta: {}, body: source };
  }

  const end = source.indexOf('\n---', 3);
  if (end === -1) {
    return { meta: {}, body: source };
  }

  const frontmatter = source.slice(4, end).trim();
  const body = source.slice(end + 4).replace(/^\n/, '');
  const meta: Partial<PageMeta> = {};

  for (const line of frontmatter.split('\n')) {
    const match = line.match(/^(\w+):\s*(.*)$/);
    if (!match) continue;
    const [, key, rawValue] = match;
    const value = rawValue.replace(/^["']|["']$/g, '').trim();
    if (key === 'title') meta.title = value;
    if (key === 'description') meta.description = value;
    if (key === 'eyebrow') meta.eyebrow = value;
  }

  return { meta, body };
}

const registry = new Map<string, PageModule>();

for (const [path, source] of Object.entries(rawPages)) {
  const slug = pathToSlug(path);
  const { meta, body } = parseFrontmatter(source);
  registry.set(slug, {
    meta: {
      title: meta.title ?? slug,
      description: meta.description ?? '',
      eyebrow: meta.eyebrow,
    },
    markdown: body,
  });
}

export function getPage(slug: string): PageModule | undefined {
  return registry.get(slug);
}

export function listSlugs(): string[] {
  return [...registry.keys()].sort();
}
